import * as _ from 'lodash';
import * as React from 'react';
import * as queryString from 'query-string';

import { followUpOnlyTitle, mobileViewTitle } from 'login-constants/titles';
import * as accountRequestAPI from 'login-apis/accountRequestAPI';
import * as activationUtils from './activationUtils';
import * as featureFlags from 'login-utils/featureFlags';
import * as stateUtils from 'login-utils/stateUtils';
import InputMap from 'login-components/Common/Inputs/InputMap';
import { utils } from 'ui-common';

import Activation from './Activation';
import connectedSimpleForm from 'login-components/SimpleForm/SimpleForm';

type BorrowerActivationProps = ReactRouter.RouteComponentProps<{}, {}>;

interface BorrowerActivationState {
  inputs: FormInput[];
}

export interface ValidateBorrowerActivationResponse {
  hasEConsented: boolean;
  isJointEmail: boolean;
  requireSSN: boolean;
}

const CORE_PASSWORD_PROPERTY = 'password';

export const generateSSNVerificationValidator = (isJointEmail: boolean) => (value: string) => {
  if (_.isNil(value) || _.size(value) !== 4) {
    return `Please enter the last 4 digits of ${
      isJointEmail ? `the primary borrower's` : 'your'
    } social security number.`;
  }
};

const termsAndPrivacyAndEConsentRequirementInput: FormInput = {
  ...InputMap.RequirementInput,
  acceptedTermsTypes: ['termsOfUse', 'privacyPolicy', 'eConsent'],
  identifier: 'termsAndEConsent',
  name: 'acceptTermsOfServiceAndEConsent',
};

const termsAndPrivacyPolicyRequirementInput: FormInput = {
  ...InputMap.RequirementInput,
  acceptedTermsTypes: ['termsOfUse', 'privacyPolicy'],
  identifier: 'terms',
  name: 'acceptTermsAndPrivacyPolicy',
};

const econsentOnlyRequirementInput: FormInput = {
  ...InputMap.RequirementInput,
  acceptedTermsTypes: ['eConsent'],
  identifier: 'econsent',
  name: 'acceptEConsent',
};

const getBorrowerActivationInputs = ({
  hasEConsented,
  isJointEmail,
  requireSSN,
}: ValidateBorrowerActivationResponse): FormInput[] => {
  const requirements = (() => {
    if (!featureFlags.isEnabled('truncatedEConsent20180817')) return [];
    // if user has econsented for all their loans, hide the `Consent to Use Electronic Signatures and Records` link
    if (hasEConsented) return [termsAndPrivacyPolicyRequirementInput];
    // TODO UI-3491: once this feature flag is removed, we will be going back to one
    // checkbox for Terms, PP, + EConsent, so you can delete the econsentOnlyRequirementInput
    return featureFlags.isEnabled('econsentOnlyCheckboxForLogin20190115')
      ? [termsAndPrivacyPolicyRequirementInput, econsentOnlyRequirementInput]
      : [termsAndPrivacyAndEConsentRequirementInput];
  })();

  return _.compact([
    {
      ...InputMap.EmailInput,
      identifier: 'email address',
      name: 'username',
      disabled: true,
      label: 'Email address',
      hidePlaceholder: true,
    },
    requireSSN && {
      ...InputMap.SSNVerificationInput,
      identifier: 'social security number verification',
      name: 'last4SSN',
      label: `Last 4 digits of ${isJointEmail ? `the primary borrower's` : 'your'} SSN (to help confirm your identity)`,
      hidePlaceholder: true,
      validate: generateSSNVerificationValidator(isJointEmail),
    },
    {
      ...InputMap.PasswordInputWithStrength,
      identifier: 'password',
      name: 'password',
      label: 'Password',
      hidePlaceholder: true,
    },
    {
      ...InputMap.PasswordInput,
      identifier: 'password',
      name: 'password2',
      placeholder: 'Confirm password',
      label: 'Confirm password',
      validateMatch: CORE_PASSWORD_PROPERTY,
      hidePlaceholder: true,
    },
    ...requirements,
  ] as FormInput[]);
};

export class BorrowerActivation extends React.Component<BorrowerActivationProps, BorrowerActivationState> {
  state = {
    inputs: [],
  };

  getBorrowerEmail = () => utils.cookieUtils.get('user_email');

  validateBorrowerActivation = () => {
    const borrowerEmail = this.getBorrowerEmail();
    return accountRequestAPI.validateBorrower(borrowerEmail);
  };

  onValidateSuccess = (res: ValidateBorrowerActivationResponse) => {
    const inputs = getBorrowerActivationInputs(res);
    this.setState({ inputs });
  };

  render() {
    const { location } = this.props;
    const queryParams = stateUtils.getOptionalCreationParams(queryString.parse(location.search), ['isFollowUpOnly']);
    const { isFollowUpOnly } = queryParams;

    const { inputs } = this.state;
    const borrowerEmail = this.getBorrowerEmail();
    const SimpleForm = _.isEmpty(inputs) ? null : connectedSimpleForm(inputs, { username: borrowerEmail });

    return (
      <div className="BorrowerActivation">
        <Activation onSuccess={this.onValidateSuccess} validateActivation={this.validateBorrowerActivation}>
          {SimpleForm && (
            <SimpleForm
              inputs={inputs}
              submitFn={activationUtils.submitBorrowerActivationRequest}
              mobileTitle={isFollowUpOnly ? followUpOnlyTitle : mobileViewTitle}
              actionText="Activate"
              title={isFollowUpOnly && followUpOnlyTitle}
            />
          )}
        </Activation>
      </div>
    );
  }
}

export default BorrowerActivation;
